import { Badge } from 'components/Badge/Badge'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { DisplayCard } from 'components/DisplayCards/DisplayCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import {
  clearShiftProgramDetails,
  getShiftProgramDetails,
  runShiftProgramNow,
} from 'features/operations/shiftProgramsSlice'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { Button, ColoredDot, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IShiftProgramRunResponse } from 'types/api/operationsShiftProgramsApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import {
  formatWeeklyDays,
  getRunStatusColor,
  getRunStatusLabel,
  getShiftProgramStatusColor,
  getShiftProgramStatusLabel,
} from '../shiftProgramsData'

const runColumns: IHonestTableColumn<IShiftProgramRunResponse>[] = [
  {
    id: 'shift',
    header: 'Shift',
    sticky: true,
    minWidth: '12rem',
    cell: (item) => (
      <Cell>
        <BodyText weight='600'>{item.shift?.name ?? '-'}</BodyText>
        <OtherText grey>{item.shiftDate}</OtherText>
      </Cell>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    minWidth: '8rem',
    cell: (item) => (
      <Cell>
        <BodyText>{getRunStatusLabel(item)}</BodyText>
      </Cell>
    ),
  },
  {
    id: 'generated',
    header: 'Generated',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.generatedCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'skipped',
    header: 'Skipped',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.skippedCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'errors',
    header: 'Errors',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.errorCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'startedAt',
    header: 'Started',
    align: 'right',
    minWidth: '10rem',
    cell: (item) => (
      <RightCell>
        <OtherText grey>{formatDateForDisplay(item.startedAt)}</OtherText>
      </RightCell>
    ),
  },
  {
    id: 'source',
    header: 'Source',
    align: 'right',
    minWidth: '8rem',
    cell: (item) => (
      <RightCell>
        <OtherText grey>{item.source}</OtherText>
      </RightCell>
    ),
  },
]

export const SingleShiftProgram = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { shiftProgramDetails } = useSelector((state: RootState) => state.shiftPrograms)
  const details = shiftProgramDetails

  useEffect(() => {
    dispatch(getShiftProgramDetails(id))
    return () => {
      dispatch(clearShiftProgramDetails())
    }
  }, [id])

  const handleRun = async () => {
    await dispatch(runShiftProgramNow(id))
  }

  const statusColor = getShiftProgramStatusColor(details?.status ?? 1)

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex center gap='1rem' width='100%' between >
          <Flex gap='1rem' center>

            <H1WithBack arrow title={details?.name ?? 'Shift Program'} />
            {details && (
              <Badge hexColor={statusColor}>
                <Flex gap='0.5rem' center>
                  <ColoredDot color={statusColor} />
                  <BodyText>{getShiftProgramStatusLabel(details.status)}</BodyText>
                </Flex>
              </Badge>
            )}
          </Flex>

          <Flex gap='1rem' >
            <Button orange onClick={() => navigate('edit')}>Edit</Button>
            {details?.status === 2 && (
              <Button orange onClick={handleRun} >
                Run
              </Button>
            )}
          </Flex>
        </Flex>

        {details && (
          <>
            <Flex gap='1rem' style={{ margin: '2rem 0' }}>
              <DisplayCard value={details.shiftCount} label='Shifts' borderColor='#ff9900' />
              <DisplayCard value={details.runCount} label='Total runs' borderColor='#ff9900' />
              <DisplayCard
                value={details.nextRunAt ? formatDateForDisplay(details.nextRunAt) : '-'}
                label='Next run'
                borderColor='#ff9900'
              />
              <DisplayCard
                value={details.lastRun ? getRunStatusLabel(details.lastRun) : 'N / A'}
                label='Last run'
                borderColor={details.lastRun ? getRunStatusColor(details.lastRun) : '#ff9900'}
              />
              <DisplayCard value={details.bindings?.length ?? 0} label='Bindings' borderColor='#ff9900' />
            </Flex>

            <Flex column gap='1rem' width='100%'>
              <Container>
                <ContainerHeader>
                  <TitleText>Identity</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>NAME</BodyText>
                      <BodyText>{details.name}</BodyText>
                    </Flex>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>NOTES</BodyText>
                      <BodyText>{details.notes || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Scope</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>SITE</BodyText>
                      <BodyText>{details.site?.name ?? '-'}</BodyText>
                    </Flex>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>TEAMS</BodyText>
                      <BodyText>{details.teams?.map((team) => team.name).join(', ') || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Recurrence</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>FREQUENCY</BodyText>
                      <BodyText>{details.recurrenceLabel}</BodyText>
                    </Flex>
                    {details.recurrenceType === 3 && (
                      <Flex gap='5rem' alignStart>
                        <BodyText grey>DAYS</BodyText>
                        <BodyText>{formatWeeklyDays(details.weeklyDays)}</BodyText>
                      </Flex>
                    )}
                    {details.recurrenceType === 4 && (
                      <Flex gap='5rem' alignStart>
                        <BodyText grey>INTERVAL</BodyText>
                        <BodyText>{details.customIntervalDays ? `${details.customIntervalDays} days` : '-'}</BodyText>
                      </Flex>
                    )}
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>SHIFTS</BodyText>
                      <BodyText>{details.shifts?.map((shift) => shift.name).join(', ') || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Task Bindings</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  {details.bindings?.length ? (
                    <Flex column gap='1rem'>
                      {details.bindings.map((binding) => (
                        <Flex key={binding.id} column gap='0.5rem'>
                          <Flex gap='5rem' alignStart>
                            <BodyText grey>TASK</BodyText>
                            <BodyText>{binding.taskTemplate?.name ?? '-'}</BodyText>
                          </Flex>
                          <Flex gap='5rem' alignStart>
                            <BodyText grey>LOCATIONS</BodyText>
                            <BodyText>
                              {binding.locations?.map((location) => location.name).join(', ') || '-'}
                            </BodyText>
                          </Flex>

                        </Flex>
                      ))}
                    </Flex>
                  ) : (
                    <BodyText grey>No bindings configured</BodyText>
                  )}
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Run history</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  {details.runs?.length ? (
                    <HonestTable columns={runColumns} data={details.runs} keyField='id' />
                  ) : (
                    <BodyText grey>No runs yet</BodyText>
                  )}
                </ContainerContent>
              </Container>

            </Flex>
          </>
        )}
      </PageWrapper>
    </>
  )
}
